import React, { useState } from 'react';
import Reveal from './Reveal';
import { Input } from './ui/input';
import { submitQuiz } from '../api';
import { quizQuestions, quizLevel } from '../quizData';
import { useBooking } from '../context/BookingContext';
import { booking } from '../mock';
import { ArrowLeft, ArrowRight, RotateCcw } from 'lucide-react';

const inputCls = 'bg-white border border-[#D6CEE0] rounded-sm px-4 h-12 text-[#241033] focus-visible:ring-1 focus-visible:ring-[#43265E] font-light';
const total = quizQuestions.length;
const scoreOf = (answers) => Math.round((answers.reduce((s, a) => s + a, 0) / (3 * total)) * 100);

const Question = ({ step, selected, onPick, onBack }) => {
  const { q, options } = quizQuestions[step];
  return (
    <div data-testid={`quiz-question-${step}`}>
      <div className="flex items-center justify-between mb-3 text-sm text-[#7E5C9E] tracking-luxe uppercase">
        <span data-testid="quiz-progress-label">Question {step + 1} of {total}</span>
        {step > 0 && <button data-testid="quiz-back-btn" onClick={onBack} className="inline-flex items-center gap-1 hover:text-[#43265E]"><ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back</button>}
      </div>
      <div className="h-1 bg-[#EDE8F2] rounded-full mb-10 overflow-hidden">
        <div className="h-full bg-[#43265E] transition-[width] duration-500" style={{ width: `${(step / total) * 100}%` }} />
      </div>
      <h3 className="font-display text-3xl text-[#241033] leading-snug mb-8">{q}</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        {options.map((opt, i) => (
          <button key={opt} data-testid={`quiz-option-${step}-${i}`} onClick={() => onPick(i)}
            className={`text-left p-5 rounded-sm border transition-colors duration-300 font-light ${selected === i ? 'border-[#43265E] bg-[#EDE8F2] text-[#241033]' : 'border-[#D6CEE0] bg-white text-[#4A4453] hover:border-[#7E5C9E]'}`}>
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
};

const Gate = ({ onSubmit, sending }) => {
  const [form, setForm] = useState({ name: '', email: '', organization: '' });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <form data-testid="quiz-gate-form" onSubmit={(e) => { e.preventDefault(); onSubmit(form); }}>
      <h3 className="font-display text-3xl text-[#241033] mb-3">Your results are ready.</h3>
      <p className="text-[#4A4453] font-light mb-8">Share where we should send them, and we’ll show your readiness score right away.</p>
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <Input data-testid="quiz-name-input" required placeholder="Full name" value={form.name} onChange={set('name')} className={inputCls} />
        <Input data-testid="quiz-email-input" required type="email" placeholder="Email address" value={form.email} onChange={set('email')} className={inputCls} />
      </div>
      <Input data-testid="quiz-org-input" placeholder="Organization (optional)" value={form.organization} onChange={set('organization')} className={inputCls + ' mb-8'} />
      <button data-testid="quiz-submit-btn" type="submit" disabled={sending} className="btn-plum rounded-full px-9 py-4 text-sm tracking-luxe uppercase inline-flex items-center gap-2 disabled:opacity-60">
        {sending ? 'Calculating…' : 'See My Score'} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
      </button>
    </form>
  );
};

const Result = ({ score, onRestart }) => {
  const { openBooking } = useBooking();
  const { level, message } = quizLevel(score);
  return (
    <div data-testid="quiz-result" className="grid md:grid-cols-[200px_1fr] gap-10 items-center">
      <div className="w-[200px] h-[200px] rounded-full border-[10px] border-[#EDE8F2] flex flex-col items-center justify-center mx-auto"
        style={{ background: `conic-gradient(#43265E ${score * 3.6}deg, #FBFAF8 0deg)` }}>
        <div className="w-[150px] h-[150px] rounded-full bg-white flex flex-col items-center justify-center">
          <span data-testid="quiz-score" className="font-display text-5xl text-[#241033]">{score}%</span>
          <span className="text-xs tracking-luxe uppercase text-[#7E5C9E]">Readiness</span>
        </div>
      </div>
      <div>
        <div data-testid="quiz-level" className="eyebrow text-[#7E5C9E] mb-3">{level}</div>
        <p className="text-[#4A4453] font-light text-lg leading-relaxed mb-8">{message}</p>
        <div className="flex flex-wrap gap-4 items-center">
          <button data-testid="quiz-book-btn" onClick={() => openBooking(booking.alignmentLab, 'Book a Session')} className="btn-plum rounded-full px-9 py-4 text-sm tracking-luxe uppercase inline-flex items-center gap-2">
            Talk Through My Results <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <button data-testid="quiz-restart-btn" onClick={onRestart} className="inline-flex items-center gap-2 text-sm tracking-luxe uppercase text-[#7E5C9E] hover:text-[#43265E]">
            <RotateCcw className="w-4 h-4" strokeWidth={1.5} /> Retake
          </button>
        </div>
      </div>
    </div>
  );
};

export const AutomationQuiz = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const score = scoreOf(answers);

  const pick = (i) => {
    const next = [...answers.slice(0, step), i];
    setAnswers(next);
    setTimeout(() => setStep(step + 1), 250);
  };

  const submit = async (form) => {
    setSending(true); setError('');
    try {
      await submitQuiz({
        ...form, score, level: quizLevel(score).level,
        answers: answers.map((a, i) => ({ question: quizQuestions[i].q, answer: quizQuestions[i].options[a] })),
      });
      setDone(true);
    } catch {
      setError('Something went wrong. Please check your details and try again.');
    } finally { setSending(false); }
  };

  const restart = () => { setStep(0); setAnswers([]); setDone(false); };

  return (
    <section className="py-28 bg-[#F4F1EC]" data-testid="automation-quiz">
      <div className="max-w-[960px] mx-auto px-6">
        <Reveal>
          <div className="eyebrow text-[#7E5C9E] mb-4">Automation Readiness Quiz</div>
          <h2 className="font-display text-4xl md:text-5xl text-[#241033] font-medium leading-tight mb-4">How ready is your organization?</h2>
          <p className="text-[#4A4453] font-light text-lg mb-12 max-w-2xl">Six quick questions. Two minutes. A clear picture of where automation and AI could create capacity for your people.</p>
        </Reveal>
        <div className="bg-white rounded-sm p-8 md:p-12 shadow-[0_30px_60px_-45px_rgba(36,16,51,0.45)]">
          {done ? <Result score={score} onRestart={restart} />
            : step < total ? <Question step={step} selected={answers[step]} onPick={pick} onBack={() => setStep(step - 1)} />
            : <Gate onSubmit={submit} sending={sending} />}
          {error && <p data-testid="quiz-error" className="text-sm text-red-700 mt-4">{error}</p>}
        </div>
      </div>
    </section>
  );
};
